import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Card, Typography, Spin, Alert } from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  SafetyCertificateOutlined, 
  UserSwitchOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import '../AuthPage.css';

const { Title, Text, Paragraph } = Typography;

/**
 * Mentor authentication page - Login only
 */
const MentorAuthPage = () => {
  const { user, isAuthenticated, isLoading, error, login, clearError, setError } = useAuth();
  const history = useHistory();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const nextUrl = new URLSearchParams(location.search).get('next') || '/admin';
      history.replace(nextUrl);
    }
  }, [isAuthenticated, user, history, location]);

  // Handle callback errors
  useEffect(() => {
    const error = new URLSearchParams(location.search).get('error');
    if (error) {
      let errorMessage = 'Authentication failed. Please try again.';
      
      switch (error) {
        case 'missing_tokens':
          errorMessage = 'Authentication tokens were not received. Please try again.';
          break;
        case 'session_validation_failed':
          errorMessage = 'Session validation failed. Please try again.';
          break;
        case 'session_validation_error':
          errorMessage = 'Error validating your session. Please try again.';
          break;
        case 'callback_error':
          errorMessage = 'Error during authentication. Please try again.';
          break;
        default:
          errorMessage = `Authentication error: ${error}. Please try again.`;
      }
      
      clearError();
      setTimeout(() => {
        setError(errorMessage);
      }, 100);
    }
  }, [location, clearError, setError]);

  // Handle OIDC login with mentor role context
  const handleOIDCLogin = () => {
    clearError();
    login("mentor");
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spin size="large" />
        <div className="auth-loading-text">
          <Text>Verifying your session...</Text>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-content">
            <div className="auth-header">
              <SafetyCertificateOutlined className="auth-icon error" />
              <Title level={2} className="auth-title">
                Authentication Error
              </Title>
            </div>
            
            <Alert
              message="Login Failed"
              description={error}
              type="error"
              showIcon
              className="auth-error"
            />
            
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button"
              icon={<UserOutlined />}
            >
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main mentor login page
  return (
    <div className="auth-container">
      <Card className="auth-card onboarding-card">
        <div className="auth-content">
          {/* Header */}
          <div className="auth-header">
            <UserSwitchOutlined className="auth-icon mentor-icon" />
            <Title level={1} className="auth-title">
              Mentor Portal
            </Title>
            <Text className="auth-subtitle">
              Guide and support your students on their learning journey
            </Text>
          </div>

          {/* Benefits */}
          <div className="onboarding-benefits">
            <Title level={4}>Mentor Tools & Features:</Title>
            <div className="benefits-list">
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Student progress monitoring and guidance</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Interactive communication with mentees</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Learning path recommendations</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Performance analytics and insights</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Resource sharing and collaboration tools</Text>
              </div>
            </div>
          </div>

          {/* Auth Actions */}
          <div className="auth-actions">
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button oidc-button primary-cta"
              icon={<LockOutlined />}
              loading={isLoading}
            >
              Sign In to Mentor Portal
            </Button>
          </div>

          {/* Security Info */}
          <div className="auth-info-section">
            <Paragraph className="auth-info">
              <SafetyCertificateOutlined /> Secure access to your mentoring dashboard
            </Paragraph>
            <Paragraph className="auth-info small">
              Your mentoring activities and student data are protected with privacy controls
            </Paragraph>
          </div>

          {/* Footer */}
          <div className="auth-footer">
            <Text type="secondary">
              Need help with mentoring tools? <a href="mailto:mentor-support@strideahead.com">Get support here</a>
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MentorAuthPage;
